import React, { useEffect, useMemo } from 'react'
import { Box, Dialog, IOption, Lego, useFetch } from '@centra/centra-ui'
import {
  changeTaskPropertyValues,
  fetchTaskProperties,
  fetchTaskPropertyOptionsList,
  getFormElements,
  getPropertyId,
  getPropertyValue,
  getTelegramPropertyOption,
} from '../utils'
import { IPropertyValue, ITask } from 'store/taskManager/model'
import { PRODUCT_PROPERTY_GID, SUPPORT_TYPE_PROPERTY_GID } from '../consts'
import { IPropertyOption, ISubmitTaskPropertyValues } from '../models'
import { useDispatch } from 'react-redux'
import { getTaskAction } from 'store/taskManager/actions'

interface IPropertyValuesEditModal {
  task: ITask
  isOpen: boolean
  onClose: () => void
}

const PropertyValuesEditModal = (props: IPropertyValuesEditModal) => {
  const { task, isOpen, onClose } = props

  const dispatch = useDispatch()

  const fetchProperties = useFetch({
    cb: fetchTaskProperties,
    errorMessage: {
      title: 'Ошибка',
      content: 'Произошла ошибка при загрузке информации о параметрах',
    },
  })

  const fetchSupportTypeOptions = useFetch({
    cb: fetchTaskPropertyOptionsList,
    errorMessage: {
      title: 'Ошибка',
      content: 'Произошла ошибка при загрузке списка типов обращения',
    },
  })

  const fetchChangePropertyValues = useFetch({
    cb: changeTaskPropertyValues,
    errorMessage: {
      title: 'Ошибка',
      content: 'Произошла ошибка при попытке изменить параметры задачи',
    },
    successMessage: {
      title: 'Выполнено',
      content: 'Операция выполнена успешно',
    },
  })

  const supportTypePropertyId = useMemo(
    () => getPropertyId(fetchProperties, SUPPORT_TYPE_PROPERTY_GID),
    [fetchProperties.result]
  )
  const productPropertyId = useMemo(
    () => getPropertyId(fetchProperties, PRODUCT_PROPERTY_GID),
    [fetchProperties.result]
  )

  const supportTypePropertyValue = useMemo(
    () => getPropertyValue(task.propertyValues, supportTypePropertyId),
    [task, supportTypePropertyId]
  ) as unknown as IPropertyValue
  const productPropertyValue = useMemo(
    () => getPropertyValue(task.propertyValues, productPropertyId),
    [task, productPropertyId]
  ) as unknown as IPropertyValue
  const telegramPropertyOption = useMemo(
    () => getTelegramPropertyOption(fetchSupportTypeOptions.result?.data?.page?.entries || []),
    [fetchSupportTypeOptions.result]
  )

  const initValue = useMemo(() => {
    const value = { clientId: task.client.id } as { [key: string]: unknown }

    if (supportTypePropertyValue?.value && Array.isArray(supportTypePropertyValue?.value)) {
      value.supportTypeId = supportTypePropertyValue.value[0].id
    }

    if (productPropertyValue?.value) {
      value.productId = productPropertyValue.value
    }

    return value
  }, [task, supportTypePropertyValue, productPropertyValue])

  const elements = useMemo(() => {
    const supportTypes = (fetchSupportTypeOptions.result?.data?.page?.entries || []).map((o: IPropertyOption) => {
      return {
        value: o.id,
        label: o.name,
        tooltip: { content: o.description },
      } as unknown as IOption<number>
    })

    return getFormElements({
      task,
      supportTypes,
      telegramSupportTypeId: telegramPropertyOption?.id,
      onClose,
    })
  }, [task, fetchSupportTypeOptions.result])

  useEffect(() => {
    fetchProperties.fetch()
  }, [])

  useEffect(() => {
    if (fetchProperties.result) {
      fetchSupportTypeOptions.fetch(supportTypePropertyId)
    }
  }, [supportTypePropertyId, fetchProperties.result])

  useEffect(() => {
    if (fetchChangePropertyValues.result) {
      dispatch(getTaskAction({ id: task.id, refreshCurrentTask: true }))
      onClose()
    }
  }, [fetchChangePropertyValues.result])

  const onSubmit = (data: any) => {
    const submitData: ISubmitTaskPropertyValues = {
      supportType: {
        id: supportTypePropertyValue?.id,
        value: data.supportTypeId,
      },
      product: {
        id: productPropertyValue?.id,
        value: data?.productId,
      },
    }

    fetchChangePropertyValues.fetch(submitData)
  }

  return (
    <Dialog open={isOpen} onClose={onClose} id={'TaskPropertyValuesEditModal'}>
      <Dialog.Header content="Редактирование параметров" />
      <Box fullWidth={true} marginTop={24}>
        <Lego
          isNewForm={true}
          elems={elements}
          onSubmit={onSubmit}
          boxWrap={{ column: true, fullWidth: true }}
          value={initValue}
        />
      </Box>
    </Dialog>
  )
}

export default PropertyValuesEditModal
